<template>
  <div>
    <div class="d-flex flex-wrap align-center mb-3">
      <h2 class="body-1 mr-auto kvm-card-title">
        <v-icon size="24" color="primary" class="pb-1" aria-hidden="true">mdi-key-variant</v-icon>
        <span class="ml-2">SSH Keys</span>
      </h2>
      <gg-btn small tile color="primary" @click="openAdd">
        <v-icon left small aria-hidden="true">mdi-plus</v-icon>Add SSH Key
      </gg-btn>
    </div>

    <p v-if="selectable" class="kvm-help mb-2">
      Tick the keys to install on this server. Keys are shared across all your services.
    </p>
    <p v-else-if="server" class="kvm-help mb-2">
      Your SSH keys are shared across all your servers. Switch a key on to install it on this server
      <template v-if="loginUser">(for <strong>{{ loginUser }}</strong>)</template>; switching it off removes it right away.
    </p>
    <v-alert v-if="server && liveError" type="info" border="left" dense class="mb-3">
      {{ liveError }}
    </v-alert>

    <div v-if="loading" class="py-6 text-center" role="status" aria-live="polite">
      <v-progress-circular indeterminate color="primary" size="32" width="3" />
      <span class="kvm-sr-only">Loading SSH keys…</span>
    </div>

    <div v-else class="kvm-key-table">
      <v-simple-table>
        <template v-slot:default>
          <caption class="kvm-sr-only">Your SSH keys</caption>
          <thead>
            <tr>
              <th v-if="selectable" scope="col" class="text-left" style="width: 48px;">
                <span class="kvm-sr-only">Select</span>
              </th>
              <th scope="col" class="text-left">Name</th>
              <th scope="col" class="text-left">Fingerprint</th>
              <th v-if="server" scope="col" class="text-left">On this server</th>
              <th scope="col" class="text-right"><span class="kvm-sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="key in keys" :key="key.id">
              <td v-if="selectable" data-label="Select">
                <v-checkbox
                  v-model="selected" :value="key.id" hide-details dense
                  class="mt-0 pt-0 kvm-key-check" :label="'Install ' + key.name"
                />
              </td>
              <td data-label="Name">{{ key.name }}</td>
              <td data-label="Fingerprint" class="text-caption kvm-fp">{{ key.fingerprint }}</td>
              <td v-if="server" data-label="On this server">
                <v-switch
                  :input-value="!!key.enabled" dense hide-details inset
                  class="mt-0 pt-0 kvm-key-switch"
                  :label="(key.enabled ? 'On' : 'Off')"
                  :aria-label="(key.enabled ? 'Remove ' : 'Install ') + key.name + ' on this server'"
                  :loading="busyKey === key.id"
                  :disabled="!!liveError || busyKey != null"
                  @change="val => toggle(key, val)"
                />
              </td>
              <td data-label="Actions" class="text-right">
                <v-btn icon small color="error" :aria-label="'Remove SSH key ' + key.name" title="Remove" @click="askDelete(key)">
                  <v-icon small aria-hidden="true">mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
            <tr v-if="!keys.length">
              <td :colspan="(selectable || server) ? 4 : 3" class="text-center kvm-help py-4 kvm-empty-cell">
                No SSH keys yet. Add one so you can log in to your server.
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </div>

    <!-- Add key dialog -->
    <v-dialog v-model="addDialog" max-width="560" persistent>
      <gg-card>
        <gg-card-text class="pa-4">
          <h2 class="body-1 mb-3 kvm-card-title">
            <v-icon color="primary" class="pb-1" aria-hidden="true">mdi-key-plus</v-icon>
            <span class="ml-1">Add SSH Key</span>
          </h2>
          <v-text-field
            v-model="form.name"
            label="Name"
            placeholder="e.g. work-laptop"
            outlined dense
            class="mb-2"
          />
          <v-textarea
            v-model="form.public_key"
            label="Public key"
            placeholder="ssh-ed25519 AAAA… you@host"
            outlined rows="4" hide-details
            class="mb-1"
          />
          <p class="kvm-help text-caption mb-3">
            Paste the contents of your <strong>public</strong> key file (e.g. id_ed25519.pub).
            Never paste a private key.
          </p>
          <div class="text-right">
            <gg-btn text class="mr-2" :disabled="submitting" @click="closeAdd">Cancel</gg-btn>
            <gg-btn tile color="primary" :loading="submitting" :disabled="!canAdd" @click="addKey">
              Add key
            </gg-btn>
          </div>
        </gg-card-text>
      </gg-card>
    </v-dialog>

    <!-- Delete confirm dialog -->
    <v-dialog v-model="deleteDialog" max-width="420" persistent>
      <gg-card>
        <gg-card-text class="pa-4">
          <h2 class="body-1 mb-2 kvm-card-title">
            <v-icon color="error" class="pb-1" aria-hidden="true">mdi-alert</v-icon>
            <span class="ml-1">Remove SSH key</span>
          </h2>
          <p v-if="server" class="mb-4">
            Remove <strong>{{ keyToDelete && keyToDelete.name }}</strong> from your account? It's removed
            from this server now. Your other servers keep it until you switch it off there.
          </p>
          <p v-else class="mb-4">
            Remove <strong>{{ keyToDelete && keyToDelete.name }}</strong>? Existing servers keep
            it until you switch it off on them; it won't be installed on new builds.
          </p>
          <div class="text-right">
            <gg-btn text class="mr-2" :disabled="submitting" @click="deleteDialog = false">Cancel</gg-btn>
            <gg-btn tile color="error" :loading="submitting" @click="deleteKey">Remove</gg-btn>
          </div>
        </gg-card-text>
      </gg-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import kvm from '@/api/kvm'

export default {
  name: 'KvmSshKeys',
  props: {
    entitlementId: { type: [String, Number], required: true },
    // When true, show per-row checkboxes and emit the selected key ids via v-model.
    selectable: { type: Boolean, default: false },
    value: { type: Array, default: () => [] },
    // The server's own SSH Keys tab: per-key live on/off switches (guest agent).
    server: { type: Boolean, default: false },
  },
  data: () => ({
    loading: true,
    keys: [],
    selected: [],
    loginUser: '', // whose authorized_keys the switches manage (server mode)
    liveError: '', // why live on/off isn't available right now (server mode)
    busyKey: null,
    addDialog: false,
    deleteDialog: false,
    keyToDelete: null,
    submitting: false,
    form: { name: '', public_key: '' },
  }),
  computed: {
    canAdd () {
      return !!this.form.name.trim() && this.form.public_key.trim().startsWith('ssh-')
    },
  },
  watch: {
    selected (val) {
      if (this.selectable) this.$emit('input', val)
    },
  },
  methods: {
    ...mapActions(['reportSuccess', 'reportError']),
    async loadKeys () {
      if (this.server) return this.loadServerKeys()
      try {
        this.loading = true
        this.keys = await kvm.getSshKeys(this.entitlementId)
        if (this.selectable) {
          // Default selection: keep what the parent passed, else select all keys.
          this.selected = this.value.length
            ? this.value.filter(id => this.keys.some(k => k.id === id))
            : this.keys.map(k => k.id)
        }
      } catch (e) {
        this.reportError(e)
      } finally {
        this.loading = false
      }
    },
    // Server mode: the account's keys with this server's live on/off state. If the
    // server can't be reached (stopped / agent not up), still list the keys.
    async loadServerKeys () {
      try {
        this.loading = true
        const res = await kvm.getServerSshKeys(this.entitlementId)
        this.keys = res.keys || []
        this.loginUser = res.user || ''
        this.liveError = ''
      } catch (e) {
        this.liveError = e.status === 409 || e.status === 503
          ? `${e.message} Keys can be switched on or off once it's reachable.`
          : e.message
        try {
          this.keys = (await kvm.getSshKeys(this.entitlementId)).map(k => ({ ...k, enabled: false }))
        } catch (e2) { this.reportError(e2) }
      } finally {
        this.loading = false
      }
    },
    async toggle (key, enabled) {
      this.busyKey = key.id
      try {
        const res = await kvm.setServerSshKey(this.entitlementId, key.id, enabled)
        key.enabled = res.enabled
        this.reportSuccess(res.enabled ? `"${key.name}" is now installed on this server.` : `"${key.name}" was removed from this server.`)
      } catch (e) {
        this.reportError(e)
        await this.loadServerKeys() // show the server's real state
      } finally {
        this.busyKey = null
      }
    },
    openAdd () {
      this.form = { name: '', public_key: '' }
      this.addDialog = true
    },
    closeAdd () {
      this.addDialog = false
    },
    async addKey () {
      try {
        this.submitting = true
        const key = await kvm.addSshKey(this.entitlementId, {
          name: this.form.name.trim(),
          public_key: this.form.public_key.trim(),
        })
        this.addDialog = false
        // On a server's own tab, a key you add goes on THIS server (only).
        if (this.server && key && !this.liveError) {
          try {
            await kvm.setServerSshKey(this.entitlementId, key.id, true)
            this.reportSuccess(`SSH key "${key.name}" added and installed on this server.`)
          } catch (e) {
            this.reportError(e)
          }
        } else {
          this.reportSuccess(`SSH key "${key.name}" added.`)
        }
        await this.loadKeys()
        // Auto-select a freshly added key when picking keys for a build.
        if (this.selectable && key && !this.selected.includes(key.id)) {
          this.selected = [...this.selected, key.id]
        }
        this.$emit('changed')
      } catch (e) {
        this.reportError(e)
      } finally {
        this.submitting = false
      }
    },
    askDelete (key) {
      this.keyToDelete = key
      this.deleteDialog = true
    },
    async deleteKey () {
      try {
        this.submitting = true
        // Take it off this server first, so deleting it from the account never
        // leaves it silently installed here.
        if (this.server && this.keyToDelete.enabled && !this.liveError) {
          await kvm.setServerSshKey(this.entitlementId, this.keyToDelete.id, false)
        }
        await kvm.deleteSshKey(this.entitlementId, this.keyToDelete.id)
        this.reportSuccess(`SSH key "${this.keyToDelete.name}" removed.`)
        this.deleteDialog = false
        this.keyToDelete = null
        await this.loadKeys()
        this.$emit('changed')
      } catch (e) {
        this.reportError(e)
      } finally {
        this.submitting = false
      }
    },
  },
  created () {
    this.loadKeys()
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.kvm-sr-only {
  @include kvm-sr-only;
}

// Section / dialog titles are real headings now; keep the body-1 look.
.kvm-card-title {
  margin: 0;
}

// Contrast-safe replacement for faint grey helper / empty-state text.
.kvm-help {
  @include kvm-secondary-text;
}

// Fingerprints: monospaced and allowed to wrap so they never overflow at 320px.
.kvm-fp {
  font-family: monospace;
  word-break: break-all;
}

// Selectable-row checkbox keeps an accessible name but hides the visible label.
.kvm-key-check ::v-deep .v-label {
  @include kvm-sr-only;
}
.kvm-key-switch ::v-deep .v-label {
  font-size: 0.85rem;
}

// Mobile: stack the keys table into cards; neutralise the off-brand teal stripe.
.kvm-key-table {
  @include kvm-stacked-table;
  @include kvm-table-destripe;

  @media (max-width: $kvm-bp-mobile) {
    ::v-deep td.kvm-empty-cell {
      justify-content: center;
      text-align: center;
    }
    ::v-deep td.kvm-empty-cell::before {
      content: none;
    }
  }
}

// Visible focus ring for the inline delete button (global focus is too weak).
.kvm-key-table ::v-deep .v-btn:focus-visible {
  @include kvm-focus-ring;
}
</style>
